import { v4 as uuidv4 } from 'uuid';
import * as ast from '../generated/ast.js';
import { invariant } from 'outvariant';
import { PropertyTypesDisplayNames } from './mappings.js';
import { FRONTEND_PROPERTY_NAME } from './constants.js';
// Utility method to generate a new int_ref value
export function getIntRef(idField) {
    const uuid = idField ? idField.id.value : uuidv4();
    return uuid.replaceAll('-', '');
}
export function isValidGuid(value) {
    const regex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return regex.test(value);
}
export function isValidGlobalVariableName(value) {
    const regex = /^[A-Z][A-Z0-9_]+$/;
    return regex.test(value);
}
export function getId(node) {
    if (!node)
        return undefined;
    return node.fields.find((field) => ast.isId(field));
}
function isPropertyValueEmptyField(property) {
    switch (true) {
        case ast.isAltTextProperty(property):
            return ast.isEmptyField(property.altText);
        case ast.isImageProperty(property):
            return ast.isEmptyField(property.image);
        case ast.isLabelProperty(property):
            return ast.isEmptyField(property.label);
        case ast.isMessageProperty(property):
            return ast.isEmptyField(property.message);
        case ast.isUrlProperty(property):
            return ast.isEmptyField(property.url);
        case ast.isUrlsProperty(property):
            return ast.isEmptyField(property.urls);
        case ast.isIconPrefixProperty(property):
            return ast.isEmptyField(property.iconPrefix);
        case ast.isIconSuffixProperty(property):
            return ast.isEmptyField(property.iconSuffix);
        case ast.isTitleProperty(property):
            return ast.isEmptyField(property.title);
        case ast.isSubTitleProperty(property):
            return ast.isEmptyField(property.subTitle);
        case ast.isOptTextProperty(property):
            return ast.isEmptyField(property.optText);
        case ast.isIosUrlProperty(property):
            return ast.isEmptyField(property.iosUrl);
        case ast.isAndroidUrlProperty(property):
            return ast.isEmptyField(property.androidUrl);
        case ast.isIosStoreProperty(property):
            return ast.isEmptyField(property.iosStore);
        case ast.isAndroidStoreProperty(property):
            return ast.isEmptyField(property.androidStore);
        case ast.isUploadLabelProperty(property):
            return ast.isEmptyField(property.uploadLabel);
        case ast.isCancelLabelProperty(property):
            return ast.isEmptyField(property.cancelLabel);
        case ast.isEndpointProperty(property):
            return ast.isEmptyField(property.endpoint);
        case ast.isActionScriptProperty(property):
            return ast.isEmptyField(property.actionScript);
        case ast.isDescriptionProperty(property):
            return ast.isEmptyField(property.description);
        case ast.isCtaLabelProperty(property):
            return ast.isEmptyField(property.ctaLabel);
        case ast.isCloseMessageProperty(property):
            return ast.isEmptyField(property.closeMessage);
        case ast.isSourceVariableProperty(property):
            return ast.isEmptyField(property.sourceVar);
        // These properties are not defined as string/empty, so
        // we don't need this check for them. Simply return false.
        case ast.isTypeStaticProperty(property):
        case ast.isTypeDynamicProperty(property):
        case ast.isDestProperty(property):
        case ast.isButtonOptionsArrayProperty(property):
        case ast.isSourceNodeProperty(property):
        case ast.isFileUploadTypeProperty(property):
        case ast.isHeadersProperty(property):
        case ast.isImageButtonOptionsArrayProperty(property):
        case ast.isListPickerOptionsArrayProperty(property):
        case ast.isPersistentMenuOptionsArrayProperty(property):
        case ast.isSetValueProperty(property):
        case ast.isQuickReplyOptionsArrayProperty(property):
        case ast.isStarOptionsArrayProperty(property):
        case ast.isInlineProperty(property):
        case ast.isVariableProperty(property):
        case ast.isValueProperty(property):
        case ast.isLoopProperty(property):
            return false;
        default:
            throw new Error(`No case defined for checking value of property ${property.$type}`);
    }
}
export function checkForDuplicateAndRequiredProperties(params) {
    const idField = getId(params.parentNode);
    const propertiesMap = {};
    params.allProperties.forEach((prop) => {
        if (propertiesMap[prop.$type]) {
            const propTypeDisplayName = PropertyTypesDisplayNames[prop.$type];
            invariant(propTypeDisplayName, `No display name mapping found for ${prop.$type}`);
            params.accept('error', `Duplicate property '${propTypeDisplayName}' found.`, {
                node: prop,
                data: {
                    id: idField?.id?.value,
                    nodeNumber: params.parentNode?.name,
                    propertyName: FRONTEND_PROPERTY_NAME.RICH_ASSET_CONTENT,
                },
            });
        }
        propertiesMap[prop.$type] = prop;
    });
    params.requiredProperties.forEach((prop) => {
        const property = propertiesMap[prop.$type];
        if (!property || isPropertyValueEmptyField(property)) {
            params.accept('error', `Missing required property '${PropertyTypesDisplayNames[prop.$type]}'.`, {
                node: params.node,
                data: {
                    id: idField?.id?.value,
                    nodeNumber: params.parentNode?.name,
                    propertyName: FRONTEND_PROPERTY_NAME.RICH_ASSET_CONTENT,
                },
            });
        }
    });
}
export function getStringValue(value) {
    if (!value || ast.isEmptyField(value))
        return '';
    if (ast.isStringField(value)) {
        return value.value.trim();
    }
    if (ast.isField(value)) {
        const str = value.value.trim();
        if (str === '""')
            return '';
        return str;
    }
    // If it is neither a string field, nor an empty field, then we are using
    // this utility function on the wrong value.
    throw new Error('Passed value is neither a StringField, nor an EmptyField.');
}
//# sourceMappingURL=utilities.js.map